import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Select, Modal, InputNumber } from 'antd';
import DataTable from '@/components/data-table';
import ClearAllFiltersButton from '@/components/data-table-actions/ClearAllFiltersButton';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { removeObjectKeys } from '@/utils/utils';
import LocalQueryParamsProvider from './LocalQueryParamsProvider';
import { useProjectMembers } from '../../_shared/hooks';
import { MemberColumns } from './MemberColumns';
import { MEMBER_ROLES, MEMBER_DEFAULT_PARAMS } from '../../_shared/constants';

function MembersListInner({
  projectId,
  employees = [],
  onAddMember,
  onUpdateMember,
  onRemoveMember,
  mutationLoading = false,
  canManage = false,
}) {
  const { t } = useTranslation(['forms', 'tables']);
  const { queryParams, setQueryParams } = useQueryParamsContext();

  const removeQueryParamsKeys = useMemo(
    () => removeObjectKeys(MEMBER_DEFAULT_PARAMS, ['limit', 'offset']),
    []
  );

  const handleFilterReset = useCallback(() => {
    setQueryParams(removeQueryParamsKeys);
  }, [setQueryParams, removeQueryParamsKeys]);

  const { data: membersData, isLoading } = useProjectMembers(
    projectId,
    queryParams
  );
  const members = membersData?.data || [];
  const totalMembers = membersData?.count || 0;

  const roleOptions = Object.values(MEMBER_ROLES).map((val) => ({
    label: t(val, { ns: 'forms' }),
    value: val,
  }));

  // Add modal state
  const [addOpen, setAddOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState(MEMBER_ROLES.MEMBER);
  const [memberRate, setMemberRate] = useState(null);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const existingIds = members.map((m) => m.employee?._id || m.employee);
  const availableEmployees = employees.filter(
    (e) => !existingIds.includes(e._id)
  );

  const handleAddMember = () => {
    if (!selectedEmployee) return;
    onAddMember(projectId, {
      employee: selectedEmployee,
      role: selectedRole,
      hourlyRate: memberRate,
    });
    setAddOpen(false);
    setSelectedEmployee(null);
    setSelectedRole(MEMBER_ROLES.MEMBER);
    setMemberRate(null);
  };

  const startEdit = (record) => {
    const empId = record.employee?._id || record.employee;
    setEditingId(empId);
    setEditValues({ role: record.role, hourlyRate: record.hourlyRate });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const saveEdit = () => {
    onUpdateMember(projectId, editingId, editValues);
    setEditingId(null);
    setEditValues({});
  };

  const columns = MemberColumns({
    members,
    canManage,
    editingId,
    editValues,
    setEditValues,
    onStartEdit: startEdit,
    onCancelEdit: cancelEdit,
    onSaveEdit: saveEdit,
    onRemove: onRemoveMember,
    projectId,
  });

  return (
    <>
      <DataTable
        data={members}
        total={totalMembers}
        title={`${t('members', { ns: 'forms' })} (${totalMembers})`}
        columns={columns}
        rowKey={(r) => r.employee?._id || r.employee}
        status={isLoading || mutationLoading ? 'pending' : 'success'}
        onChangePagination={(key, value) => setQueryParams({ [key]: value })}
        offset={queryParams.offset}
        limit={queryParams.limit}
        headerChildren={
          <>
            <ClearAllFiltersButton
              label={t('clearAllFilters', { ns: 'tables' })}
              onReset={handleFilterReset}
              defaultParams={removeQueryParamsKeys}
            />
            {canManage && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddOpen(true)}
                className="btn-primary !h-8 !border !border-white"
              >
                {t('addMember', { ns: 'forms' })}
              </Button>
            )}
          </>
        }
      />

      <Modal
        title={t('addMember', { ns: 'forms' })}
        open={addOpen}
        onOk={handleAddMember}
        onCancel={() => setAddOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button className="btn-default" onClick={() => setAddOpen(false)}>
              {t('cancel', { ns: 'forms' })}
            </Button>
            <Button
              className="btn-primary"
              disabled={!selectedEmployee}
              onClick={handleAddMember}
            >
              {t('save', { ns: 'forms' })}
            </Button>
          </div>
        }
        destroyOnClose
        width={420}
      >
        <div className="flex flex-col gap-3">
          <div>
            <label className="mb-1 block text-sm">
              {t('employee', { ns: 'tables' })}
            </label>
            <Select
              className="w-full"
              showSearch
              optionFilterProp="label"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              options={availableEmployees.map((e) => ({
                label: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
                value: e._id,
              }))}
              placeholder={t('selectEmployee', { ns: 'forms' })}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm">
              {t('role', { ns: 'tables' })}
            </label>
            <Select
              className="w-full"
              value={selectedRole}
              onChange={setSelectedRole}
              options={roleOptions}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm">
              {t('hourlyRate', { ns: 'forms' })}
            </label>
            <InputNumber
              className="w-full"
              min={0}
              value={memberRate}
              onChange={setMemberRate}
              placeholder={t('optional', { ns: 'forms' })}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export default function MembersList(props) {
  return (
    <LocalQueryParamsProvider defaultParams={MEMBER_DEFAULT_PARAMS} prefix="m_">
      <MembersListInner {...props} />
    </LocalQueryParamsProvider>
  );
}
